import { PiFilePdf, PiFileVideo, PiFileImage, PiFileDoc } from 'react-icons/pi';
import { MdClose, MdDeleteOutline, MdOutlineSaveAlt, MdOutlineVisibility } from 'react-icons/md';
import { Chip, cn, IconButton, Tooltip, useDisclosure } from '@ds';
import { PdfPreviewModal } from './PdfPreviewModal';

const HintChip = ({ text, label = 'Redacted' }: { text?: string; label?: string }) => {
  const chip = (
    <Chip size="sm" variant="flat">
      {label}
    </Chip>
  );

  return text ? <Tooltip content={text}>{chip}</Tooltip> : chip;
};

const isPdf = (filename: string = '') => filename.split('.').pop()?.toLowerCase() === 'pdf';

export const renderFileExtensionImage = (props: { className: string }, filename: string = '') => {
  const fileExtension = filename.split('.')?.pop()?.toLowerCase() || '';
  const imageExtensions = ['jpg', 'jpeg', 'png', 'heic', 'svg'];
  const videoExtensions = ['mov', 'mp4', 'hevc'];

  if (imageExtensions.includes(fileExtension)) return <PiFileImage {...props} />;
  if (videoExtensions.includes(fileExtension)) return <PiFileVideo {...props} />;
  if (fileExtension === 'pdf') return <PiFilePdf {...props} />;
  return <PiFileDoc {...props} />;
};

export type TMDXFile = {
  id?: string;
  fileName: string;
  path: string;
  file?: File;
  signedUrl?: string;
};

export type UploadedFileProps = {
  file: TMDXFile;
  showDownloadBtn?: boolean;
  showDeleteBtn?: boolean;
  showPreviewBtn?: boolean;
  containerClassName?: string;
  onDelete: (file: TMDXFile) => void;
  showHint?: boolean;
  hintText?: string;
  hintLabel?: string;
};

export const RegularUploadedFile = ({
  file,
  onDelete,
  showDownloadBtn,
  showDeleteBtn,
  showPreviewBtn = false,
  containerClassName = '',
  showHint,
  hintText,
  hintLabel,
}: UploadedFileProps) => {
  const fileExtensionProps = { className: 'text-base-30', size: 24 };
  const { fileName, signedUrl } = file;
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onOpenChange: onPreviewOpenChange } = useDisclosure();
  const canPreview = showPreviewBtn && isPdf(fileName) && !!signedUrl;

  return (
    <div className={cn('flex items-center py-2 px-3 gap-2 bg-base-60 rounded-lg', containerClassName)}>
      {renderFileExtensionImage(fileExtensionProps, fileName)}
      <div className="flex-1 truncate">{fileName}</div>
      {showHint && <HintChip text={hintText} label={hintLabel} />}
      {canPreview && (
        <IconButton
          size="sm"
          variant="light"
          aria-label="Preview"
          onClick={onPreviewOpen}
          startContent={<MdOutlineVisibility size={20} />}
        />
      )}
      {showDownloadBtn && (
        <IconButton
          as="a"
          size="sm"
          variant="light"
          href={file.signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          startContent={<MdOutlineSaveAlt size={20} />}
        />
      )}
      {showDeleteBtn && (
        <IconButton
          size="sm"
          variant="light"
          onClick={() => onDelete(file)}
          startContent={<MdDeleteOutline size={24} />}
        />
      )}
      {canPreview && (
        <PdfPreviewModal
          isOpen={isPreviewOpen}
          onOpenChange={onPreviewOpenChange}
          fileName={fileName}
          signedUrl={signedUrl}
        />
      )}
    </div>
  );
};

export const RoundedUploadedFile = ({
  file,
  onDelete,
  showDownloadBtn,
  showDeleteBtn,
  showPreviewBtn = false,
  containerClassName = '',
  showHint,
  hintText,
  hintLabel,
}: UploadedFileProps) => {
  const fileExtensionProps = { className: 'text-base-10', size: 24 };
  const { fileName, signedUrl } = file;
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onOpenChange: onPreviewOpenChange } = useDisclosure();
  const canPreview = showPreviewBtn && isPdf(fileName) && !!signedUrl;

  return (
    <div
      className={cn(
        'flex border-1.5 border-base-10 items-center py-2 px-3 h-9 gap-2 rounded-full max-w-[255px]',
        containerClassName,
      )}
    >
      {renderFileExtensionImage(fileExtensionProps, fileName)}
      <div className="flex-1 truncate font-medium">{fileName}</div>
      {showHint && <HintChip text={hintText} label={hintLabel} />}
      {canPreview && (
        <IconButton
          size="sm"
          aria-label="Preview"
          onClick={onPreviewOpen}
          startContent={<MdOutlineVisibility size={20} />}
        />
      )}
      {showDownloadBtn && (
        <IconButton
          as="a"
          size="sm"
          href={file.signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          startContent={<MdOutlineSaveAlt size={20} />}
        />
      )}

      {showDeleteBtn && (
        <IconButton size="sm" variant="light" onClick={() => onDelete(file)} startContent={<MdClose size={24} />} />
      )}
      {canPreview && (
        <PdfPreviewModal
          isOpen={isPreviewOpen}
          onOpenChange={onPreviewOpenChange}
          fileName={fileName}
          signedUrl={signedUrl}
        />
      )}
    </div>
  );
};
